import React, { useState, useEffect } from 'react';
import '../css/StaticTable.css';

const API_URL = process.env.REACT_APP_URL;

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(value)) return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return '-';
  return new Intl.NumberFormat('en-IN').format(number);
};

const groupByDistrict = (rows) =>
  Object.values(
    rows.reduce((acc, row) => {
      if (!acc[row.district]) {
        acc[row.district] = { district: row.district, assemblies: [] };
      }
      acc[row.district].assemblies.push({
        name: row.assembly_constituency,
        registered: row.total_registered,
        verified: row.total_verified,
        trainedSurveyors: row.total_trained_surveyors,
        boothMapped: row.total_booth_mapped,
        activeSurveyors: row.total_active_surveyors,
        boothCount: row.total_booth_count,
        target: row.target,
        surveyDone: row.total_survey_done,
        achievedPercent: parseFloat(row.achieved_percent),
      });
      return acc;
    }, {})
  );

const getDistrictTotals = (assemblies) =>
  assemblies.reduce(
    (acc, a) => {
      acc.registered += Number(a.registered) || 0;
      acc.verified += Number(a.verified) || 0;
      acc.trainedSurveyors += Number(a.trainedSurveyors) || 0;
      acc.boothMapped += Number(a.boothMapped) || 0;
      acc.activeSurveyors += Number(a.activeSurveyors) || 0;
      acc.boothCount += Number(a.boothCount) || 0;
      acc.target += Number(a.target) || 0;
      acc.surveyDone += Number(a.surveyDone) || 0;
      return acc;
    },
    { registered: 0, verified: 0, trainedSurveyors: 0, boothMapped: 0, activeSurveyors: 0, boothCount: 0, target: 0, surveyDone: 0 }
  );

const DynamicTable = () => {
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState({});
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null, // 'district', 'registered', 'verified', 'trainedSurveyors', 'boothMapped', 'activeSurveyors', 'boothCount', 'target', 'surveyDone', 'achievedPercent'
    direction: 'asc' // 'asc' or 'desc'
  });

  const loadData = () => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/parse-constituency-report`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setDistrictData(groupByDistrict(json.data));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => { loadData(); }, []);

  const toggleDistrict = (district) => {
    setOpened((prev) => ({ ...prev, [district]: !prev[district] }));
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted districts
  const getSortedDistricts = () => {
    if (!sortConfig.key) return districtData;

    return [...districtData].sort((a, b) => {
      const totalsA = getDistrictTotals(a.assemblies);
      const totalsB = getDistrictTotals(b.assemblies);
      
      let valueA, valueB;
      
      switch (sortConfig.key) {
        case 'district':
          valueA = a.district;
          valueB = b.district;
          if (sortConfig.direction === 'asc') {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
          
        case 'registered':
          valueA = totalsA.registered;
          valueB = totalsB.registered;
          break;
          
        case 'verified':
          valueA = totalsA.verified;
          valueB = totalsB.verified;
          break;
          
        case 'trainedSurveyors':
          valueA = totalsA.trainedSurveyors;
          valueB = totalsB.trainedSurveyors;
          break;
          
        case 'boothMapped':
          valueA = totalsA.boothMapped;
          valueB = totalsB.boothMapped;
          break;
          
        case 'activeSurveyors':
          valueA = totalsA.activeSurveyors;
          valueB = totalsB.activeSurveyors;
          break;
          
        case 'boothCount':
          valueA = totalsA.boothCount;
          valueB = totalsB.boothCount;
          break;
          
        case 'target':
          valueA = totalsA.target;
          valueB = totalsB.target;
          break;
          
        case 'surveyDone':
          valueA = totalsA.surveyDone;
          valueB = totalsB.surveyDone;
          break;
          
        case 'achievedPercent':
          const percentA = totalsA.target > 0 ? (totalsA.surveyDone / totalsA.target) * 100 : 0;
          const percentB = totalsB.target > 0 ? (totalsB.surveyDone / totalsB.target) * 100 : 0;
          valueA = percentA;
          valueB = percentB;
          break;
          
        default:
          return 0;
      }
      
      if (sortConfig.direction === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return ' ⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const sortedDistricts = getSortedDistricts();

  // Column configuration
  const columns = [
    { key: 'district', label: 'District', sortable: true },
    { key: 'assembly', label: 'Assembly Constituency', sortable: false },
    { key: 'registered', label: 'Total Registered', sortable: true },
    { key: 'verified', label: 'Total Verified', sortable: true },
    { key: 'trainedSurveyors', label: 'Total Trained Surveyors', sortable: true },
    { key: 'boothMapped', label: 'Total Booth Mapped', sortable: true },
    { key: 'activeSurveyors', label: 'Total Active Surveyors', sortable: true },
    { key: 'boothCount', label: 'Total Booth Count', sortable: true },
    { key: 'target', label: 'Target', sortable: true },
    { key: 'surveyDone', label: 'Total Survey Done', sortable: true },
    { key: 'achievedPercent', label: 'Achieved %', sortable: true }
  ];

  return (
    <div className="dashboard-container">
      <div className="table-section-title">
        <span className="section-number">B.</span>
        <h2>DISTRICT / ASSEMBLY WISE DETAILS</h2>
      </div>

      {loading && <p className="table-status">Loading...</p>}
      {error && <p className="table-status">Failed to load data: {error}</p>}

      {!loading && !error && (
        <div className="table-wrapper">
          <table className="dynamic-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={column.sortable ? 'sortable-header' : ''}
                    onClick={() => column.sortable && handleSort(column.key)}
                    style={column.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}}
                  >
                    {column.label} {column.sortable && getSortIcon(column.key)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedDistricts.map(({ district, assemblies }) => {
                const totals = getDistrictTotals(assemblies);
                const achievedPercent =
                  totals.target > 0
                    ? Number(((totals.surveyDone / totals.target) * 100).toFixed(2))
                    : 0;

                return (
                  <React.Fragment key={district}>
                    <tr className="district-row">
                      <td style={{minWidth:'150px',textAlign:'left', display: 'flex', alignItems:'center'}}>
                        <button
                          type="button"
                          className="expand-button"
                          onClick={() => toggleDistrict(district)}
                        >
                          {opened[district] ? '−' : '+'}
                        </button>
                        <span className="district-name">{district}</span>
                      </td>
                      <td className="muted">District total</td>
                      <td>{formatNumber(totals.registered)}</td>
                      <td>{formatNumber(totals.verified)}</td>
                      <td>{formatNumber(totals.trainedSurveyors)}</td>
                      <td>{formatNumber(totals.boothMapped)}</td>
                      <td>{formatNumber(totals.activeSurveyors)}</td>
                      <td>{formatNumber(totals.boothCount)}</td>
                      <td>{formatNumber(totals.target)}</td>
                      <td>{formatNumber(totals.surveyDone)}</td>
                      <td>{achievedPercent}%</td>
                    </tr>

                    {opened[district] &&
                      assemblies.map((assembly) => (
                        <tr className="assembly-row" key={`${district}-${assembly.name}`}>
                          <td />
                          <td className="assembly-name">{assembly.name}</td>
                          <td>{formatNumber(assembly.registered)}</td>
                          <td>{formatNumber(assembly.verified)}</td>
                          <td>{formatNumber(assembly.trainedSurveyors)}</td>
                          <td>{formatNumber(assembly.boothMapped)}</td>
                          <td>{formatNumber(assembly.activeSurveyors)}</td>
                          <td>{formatNumber(assembly.boothCount)}</td>
                          <td>{formatNumber(assembly.target)}</td>
                          <td>{formatNumber(assembly.surveyDone)}</td>
                          <td>{assembly.achievedPercent}%</td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;